import type { ManagedQueueListItem } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useDeleteQueue,
  useInactiveQueues,
  useUpdateQueue,
} from '@/products/helpdesk/features/tickets/use-tickets';
import { ApiRequestError } from '@/lib/api';

export function InactiveQueuesPanel({ enabled }: { enabled: boolean }) {
  const inactive = useInactiveQueues({ enabled });
  const updateQueue = useUpdateQueue();
  const deleteQueue = useDeleteQueue();
  const [pendingDelete, setPendingDelete] = useState<ManagedQueueListItem | null>(null);

  if (!enabled) {
    return null;
  }

  async function reactivate(queue: ManagedQueueListItem) {
    try {
      await updateQueue.mutateAsync({ id: queue.id, body: { isActive: true } });
      toast.success(`„${queue.name}“ wurde reaktiviert.`);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Warteschlange konnte nicht reaktiviert werden.',
      );
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) {
      return;
    }
    try {
      await deleteQueue.mutateAsync(pendingDelete.id);
      toast.success(`„${pendingDelete.name}“ wurde endgültig gelöscht.`);
      setPendingDelete(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Warteschlange konnte nicht gelöscht werden.',
      );
    }
  }

  const items = inactive.data?.items ?? [];
  const busy = updateQueue.isPending || deleteQueue.isPending;
  const showPanel = items.length > 0;

  if (!enabled || (!showPanel && !pendingDelete)) {
    return null;
  }

  return (
    <>
      {showPanel && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Inaktive Warteschlangen</CardTitle>
            <CardDescription>
              Deaktivierte Spalten erscheinen nicht auf dem Board. Reaktivieren stellt sie wieder
              her. Endgültiges Löschen ist nur möglich, wenn keine Tickets mehr zugeordnet sind.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {items.map((queue) => (
                <li
                  key={queue.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <p className="font-medium">{queue.name}</p>
                    <p className="text-xs text-muted-foreground">
                      <span className="font-mono">{queue.slug}</span>
                      {' · '}
                      {queue.ticketCount === 0
                        ? 'Keine Tickets'
                        : `${queue.ticketCount} Ticket${queue.ticketCount === 1 ? '' : 's'} (${queue.openCount} offen)`}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => void reactivate(queue)}
                    >
                      Reaktivieren
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy || !queue.canDelete}
                      title={
                        queue.canDelete
                          ? undefined
                          : 'Löschen erst möglich, wenn keine Tickets mehr zugeordnet sind.'
                      }
                      onClick={() => setPendingDelete(queue)}
                    >
                      Endgültig löschen
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Warteschlange endgültig löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete
                ? `„${pendingDelete.name}“ wird unwiderruflich entfernt. Kategorie- und SLA-Verweise auf diese Warteschlange werden geleert.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteQueue.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteQueue.isPending}
              onClick={(event) => {
                event.preventDefault();
                void confirmDelete();
              }}
            >
              {deleteQueue.isPending ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  Wird gelöscht …
                </>
              ) : (
                'Endgültig löschen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
